import React from 'react'
import {StackNavigationProp} from '@react-navigation/stack'
import {useNavigation} from '@react-navigation/native'

import {StateSelectTemplate} from 'src/products/general/StateSelect/StateSelectTemplate'
import {usePageViewedAnalytics} from 'src/lib/Analytics/usePageViewedAnalytics'
import {AppEvents} from 'src/lib/Analytics/app_events'
import {TrackAppEvent} from 'src/lib/Analytics/analytics_compat'
import {useCassandraMutation} from '@possible/cassandra/src/utils/hooks'
import {UserSetResidenceStateDocument} from 'src/products/general/StateSelect/queries/UserSetResidenceState.gqls'
import {StateSelectFormType} from 'src/products/general/StateSelect/StateSelect.types'
import Log from 'src/lib/loggingUtil'
import {isAdvanceState} from 'src/lib/advance/CashAdvanceUtils'
import {useCanAccessCards} from 'src/products/card/LoanDash/useCanAccessCards'
import {MainStackParamList} from 'src/nav/MainStackParamsList'
import {usePromise} from 'src/lib/usePromise/usePromise'
import {allowedStates} from 'src/lib/us_states'

type StateSelectGQLContainer = {
  onComplete: () => Promise<void>
}

const StateSelectGQLContainer: React.FC<StateSelectGQLContainer> = (props): JSX.Element => {
  const {onComplete} = props

  const navigation = useNavigation<StackNavigationProp<MainStackParamList>>()
  const {canAccessCards} = useCanAccessCards()

  const [selectState, {loading: isLoadingStateSelect}] = useCassandraMutation(
    UserSetResidenceStateDocument,
    {
      onError: (error) => {
        Log.error(`${error.message} - StateSelect Screen - UserSetResidenceStateDocument mutation`)
      },
    },
  )

  usePageViewedAnalytics({
    eventCategory: AppEvents.Category.Application,
    eventName: AppEvents.Name.state_selector_viewed,
  })

  const [onSubmit, {isLoading: isLoadingOnSubmit}] = usePromise(
    async ({state}: StateSelectFormType): Promise<void> => {
      if (state === undefined) {
        return
      }

      await selectState({variables: {state}})

      TrackAppEvent(AppEvents.Name.state_selector_submitted, AppEvents.Category.Application, {
        state,
      })

      if (isAdvanceState(state, canAccessCards)) {
        navigation.navigate('AdvanceSplashScreen')

        /* We have to manually navigate users in unsupported states to the waitlist because if we
         * send them to Workflows which also has logic to handle this, they wont have any prereqs
         * and `useSetupRouteHistory` throws a Workflow error. */
      } else if (!allowedStates().includes(state)) {
        navigation.reset({
          index: 0,
          routes: [{name: 'UnsupportedStateWaitList'}],
        })
      } else {
        await onComplete()
      }
    },
  )

  /* This is necessary because the type of the handler returned by usePromise is not compatible
   * with with hook form's `handleSubmit`.*/
  const handleOnSubmit = async ({state}: StateSelectFormType): Promise<void> => {
    await onSubmit({state})
  }

  return (
    <StateSelectTemplate
      onSubmit={handleOnSubmit}
      isLoading={isLoadingStateSelect || isLoadingOnSubmit}
    />
  )
}

export {StateSelectGQLContainer}
